import { DownOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Menu,
  message,
  Modal,
  Space,
  Tooltip,
} from "antd";
import { MenuInfo } from "rc-menu/lib/interface";
import { RangeValue } from "rc-picker/lib/interface";
import moment, { Moment } from "moment";
import React, { Dispatch, useEffect, useState } from "react";
import { useEndTime, useStartTime } from "../DateContext/DateContext";

const { RangePicker } = DatePicker;

const defaultTimeFormat = "YYYY-MM-DDTHH:mm:ssZ";
const dateFormat = "MM/DD/YY";

interface Props {
  isIndependentDateSelector?: boolean;
  endDate?: string;
  setEndDate?: Dispatch<React.SetStateAction<string>>;
  startDate?: string;
  setStartDate?: Dispatch<React.SetStateAction<string>>;
  handleFilterReset?: () => void;
  loadPageData?: (start: string, end: string) => void;
  rangeOfSelector?: {
    range: "day" | "months";
    selectorDefaultOptions: {
      value: number;
      disabled: boolean;
      warningMsg?: string;
    }[];
  };
  showLoadingAlert?: boolean;
  displayShowAllOption?: boolean;
  customOption?: { disabled: boolean; warningMsg?: string };
}

/**
 * DateRangePicker - Generic date picker, it is set to select in the range of days modifying the global state startTime and endTime
 * General date picker component
 * @param rangeOfSelector - specify the selection range (day or month), in the array you enter the number of options you want in the multi selector.
 * @param handleFilterReset - optional function to reset filters
 * @param loadPageData - optional function to obtain all data
 *
 * @param isIndependentDateSelector - modifies the global startTime and endTime state or not. Usually you want to set it to true when you want to pull data by date regardless of the global status. If true, the following params are mandatory:
 * @param endDate
 * @param setEndDate
 * @param startDate
 * @param setStartDate
 * @param showLoadingAlert - controls whether or not a "Loading..." pop up should be displayed when selecting a new option
 * @param displayShowAllOption - Right now this option is only supported when isIndependentDateSelector is active as well. It allows to disable the date range.
 */

const DateRangePicker = ({
  isIndependentDateSelector = false,
  displayShowAllOption = false,
  showLoadingAlert = true,
  rangeOfSelector = {
    range: "day",
    selectorDefaultOptions: [
      { value: 7, disabled: false },
      {
        value: 30,
        disabled: true,
        warningMsg: "This is restricted to Pro or Business users",
      },
    ],
  },
  customOption = {
    disabled: true,
    warningMsg: "This is restricted to Pro or Business users",
  },
  endDate,
  setEndDate,
  startDate,
  setStartDate,
  handleFilterReset,
  loadPageData,
}: Props) => {
  // date from react context, it is the time of the global state
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();

  // Can not select days after today
  const disabledDate = (current: moment.Moment) => {
    return current && current > moment().endOf("day");
  };

  // funtion that handles change of range
  const changeDates = async (startParam: string, endParam: string) => {
    if (handleFilterReset) {
      handleFilterReset();
    }
    const start = moment(startParam).format(dateFormat);
    const end = moment(endParam).format(dateFormat);

    if (isIndependentDateSelector && setStartDate && setEndDate) {
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartTime(start);
      setEndTime(end);
    }
    if (loadPageData) {
      await loadPageData(start, end);
    }
  };

  // Generate initial placeholder text for selector
  const diffDays = () => {
    if (isIndependentDateSelector && startDate === "" && endDate === "") {
      return "Show all";
    }
    const now = moment();
    // Selections can be done in the range of days or months, default is days
    if (rangeOfSelector.range === "day") {
      const days = now.diff(
        isIndependentDateSelector ? startDate : startTime,
        "day"
      );
      // over 31 days the placeholder changes to MM/DD/YY - MM//DD/YY format
      if (days > 31) {
        return `${moment(
          // if isIndependentDateSelector is true, local state is updated, otherwise it would be the global state
          isIndependentDateSelector ? startDate : startTime
        ).format(dateFormat)} - ${moment(
          isIndependentDateSelector ? endDate : endTime
        ).format(dateFormat)}`;
      }
      return `Past ${days} days`;
    } else {
      const months = now.diff(
        isIndependentDateSelector ? startDate : startTime,
        "months"
      );
      // over 12 months or less than a month the placeholder changes to MM/DD/YY - MM//DD/YY format
      if (months === 0 || months > 12)
        return `${moment(
          isIndependentDateSelector ? startDate : startTime
        ).format(dateFormat)} - ${moment(
          isIndependentDateSelector ? endDate : endTime
        ).format(dateFormat)}`;
      return `Past ${months} months`;
    }
  };

  const [datePickerText, setDatePickerText] = useState(diffDays());
  const [isTimeModalVisible, setIsTimeModalVisible] = useState(false);

  // By clicking an option from the menu (except the "custom" one) dates are updated by passing the date from the past and today's date to function changeDates
  function handleMenuClick(e: MenuInfo) {
    window.analytics.track("Date changed");
    if (showLoadingAlert) {
      message.info("Loading..", 4);
    }
    changeDates(
      moment()
        .subtract(parseInt(e.key), rangeOfSelector.range)
        .format(defaultTimeFormat),
      moment().add(1, "days").format(defaultTimeFormat)
    );
    setDatePickerText(
      `Past ${e.key} ${rangeOfSelector.range === "months" ? "months" : "days"}`
    );
  }

  // show custom date range selector
  const showTimeModal = () => {
    window.analytics.track("Custom time");
    setIsTimeModalVisible(true);
  };

  // close modal
  const handleCancel = async () => {
    if (showLoadingAlert) {
      message.info("Loading..", 4);
    }
    setDatePickerText(
      `${moment(isIndependentDateSelector ? startDate : startTime).format(
        dateFormat
      )} - ${moment(isIndependentDateSelector ? endDate : endTime).format(
        dateFormat
      )}`
    );
    setIsTimeModalVisible(false);
    if (loadPageData) {
      await loadPageData(startTime, endTime);
    }
  };

  // global or local date state is updated (depending on parameter isIndependentDateSelector)
  const dateChanged = async (date: RangeValue<Moment>) => {
    // it checks if start and end date is obtained by the date range picker
    if (date && date[0] && date[1]) {
      const start = date[0].format(dateFormat);
      const end = date[1].format(dateFormat);
      // if is independent selector, dates will be stored in the state from parent component
      if (isIndependentDateSelector && setStartDate && setEndDate) {
        setStartDate(start);
        setEndDate(end);
        if (handleFilterReset) {
          handleFilterReset();
        }
      } else {
        // otherwise it will modify the global state
        setStartTime(start);
        setEndTime(end);
        if (handleFilterReset) {
          handleFilterReset();
        }
      }
      // Set placeholder text in selector
      setDatePickerText(
        `${moment(isIndependentDateSelector ? startDate : startTime).format(
          dateFormat
        )} - ${moment(isIndependentDateSelector ? endDate : endTime).format(
          dateFormat
        )}`
      );
    }
  };

  // selectable options, Is possible to have any number of option it just have to be specified on rangeOfSelector.range array
  const menu = (
    <Menu>
      {rangeOfSelector.selectorDefaultOptions.map((item) => (
        <Menu.Item
          key={item.value}
          onClick={handleMenuClick}
          disabled={item.disabled}
        >
          <Tooltip
            title={item.disabled ? item.warningMsg : ""}
            placement="left"
            mouseLeaveDelay={0}
            overlayStyle={{ width: "160px" }}
          >
            <p className="w-full m-0">
              Past{" "}
              {`${item.value} ${
                rangeOfSelector.range === "months" ? "months" : "days"
              }`}
            </p>
          </Tooltip>
        </Menu.Item>
      ))}
      <Menu.Item
        key="custom"
        onClick={showTimeModal}
        disabled={customOption.disabled}
      >
        <Tooltip
          title={customOption.disabled ? customOption.warningMsg : ""}
          placement="left"
          mouseLeaveDelay={0}
          overlayStyle={{ width: "160px" }}
        >
          <p className="w-full m-0">Custom</p>
        </Tooltip>
      </Menu.Item>
      {displayShowAllOption &&
        isIndependentDateSelector &&
        setStartDate &&
        setEndDate && (
          <Menu.Item
            key="custom"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setDatePickerText("Show all");
            }}
          >
            Show All
          </Menu.Item>
        )}
    </Menu>
  );

  return (
    <div>
      <Space>
        <Dropdown className="joyride-date" overlay={menu}>
          <Button>
            {datePickerText} <DownOutlined />
          </Button>
        </Dropdown>
        <Modal
          width={400}
          title="Select date range "
          style={{ top: 40, right: 10 }}
          visible={isTimeModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button key="submit" onClick={handleCancel}>
              Ok
            </Button>,
          ]}
        >
          <RangePicker
            defaultValue={[
              moment(
                isIndependentDateSelector ? startDate || startTime : startTime,
                dateFormat
              ),
              moment(
                isIndependentDateSelector ? endDate || endTime : endTime,
                dateFormat
              ),
            ]}
            format={dateFormat}
            disabledDate={disabledDate}
            onChange={dateChanged}
          />
        </Modal>
      </Space>
    </div>
  );
};

export default DateRangePicker;
